import { createListenerMiddleware, isAnyOf, type PayloadAction } from '@reduxjs/toolkit'

import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type { EnhancedMarketId } from '#api/enhancements/enhancedTmsApiTypes'
import type {
  DeleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdApiArg,
  DeleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdApiArg,
  DeleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionIdApiArg,
  PostApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesApiArg,
  PostApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsApiArg,
  PostApiAdmintoolMarketsByMarketIdGroupsApiArg,
  PutApiAdmintoolMarketsByMarketIdGroupsAndGroupIdApiArg,
  PutApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdApiArg,
  PutApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionIdApiArg,
} from '#api/tmsApiService.ts'
import { makeSerializedError } from '#features/ErrorHandling/errorHelpers'
import { toastError } from '#features/ErrorHandling/toastError'
import {
  _addPendingTask,
  _pushOrReplaceFamily,
  _pushOrReplaceGroup,
  _pushOrReplaceVariant,
  _removePendingTask,
  _setMarketStatus,
  _setNationalCodes,
  loadNationalCodesForMarket,
} from '#store/nationalCodesSlice.ts'
import { store } from '#store/root'

export const nationalCodesObserver = (() => {
  const middleware = createListenerMiddleware()
  middleware.startListening({
    matcher: isAnyOf(loadNationalCodesForMarket),
    effect: async (action, _listenerApi) => {
      const state = store.getState()
      if (loadNationalCodesForMarket.match(action)) {
        const marketId = action.payload?.marketId
        if (!marketId) {
          return
        }

        if (state.nationalCodes?.byMarket[marketId]?.status === 'loaded' && !action.payload?.forceReload) {
          return
        }

        try {
          store.dispatch(_setMarketStatus({ marketId, status: 'loading' }))
          const request = store.dispatch(enhancedTmsApiService.endpoints.getApiAdmintoolMarketsByMarketId.initiate({ marketId, recursive: true }))
          const result = await request.unwrap()

          if (!result.market) {
            throw new Error(`Market with ID ${marketId} not found`)
          }

          store.dispatch(_setNationalCodes({ marketId, nationalCodes: result.market.groups || [] }))
          store.dispatch(_setMarketStatus({ marketId, status: 'loaded' }))
        } catch (e) {
          store.dispatch(_setMarketStatus({ marketId, status: 'failed', error: makeSerializedError(e) }))
        }
      }
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.postApiAdmintoolMarketsByMarketIdGroups.matchFulfilled,
    effect: (action, api) => {
      // console.log('Done POST /admintool/markets/{marketId}/groups', action, api)
      const originalArgs = action.meta.arg.originalArgs as PostApiAdmintoolMarketsByMarketIdGroupsApiArg
      const marketId = originalArgs.marketId as EnhancedMarketId
      const returnValue = action.payload.group
      if (!returnValue) {
        toastError('No group returned from POST /admintool/markets/{marketId}/groups')
        return
      }
      const { families: _families, ...group } = returnValue

      api.dispatch(
        _pushOrReplaceGroup({
          marketId,
          groupId: originalArgs.adminNatCodesNewGroupRequest.groupId,
          group,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.putApiAdmintoolMarketsByMarketIdGroupsAndGroupId.matchFulfilled,
    effect: (action, api) => {
      // console.log('Done PUT /admintool/markets/{marketId}/groups/{groupId}', action, api)

      const originalArgs = action.meta.arg.originalArgs as PutApiAdmintoolMarketsByMarketIdGroupsAndGroupIdApiArg
      const returnValue = action.payload.group
      if (!returnValue) {
        toastError('No group returned from PUT /admintool/markets/{marketId}/groups/{groupId}')
        return
      }

      const marketId = originalArgs.marketId as EnhancedMarketId
      const groupId = originalArgs.groupId
      const { families: _families, ...group } = returnValue

      api.dispatch(
        _pushOrReplaceGroup({
          marketId,
          groupId,
          group,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.deleteApiAdmintoolMarketsByMarketIdGroupsAndGroupId.matchFulfilled,
    effect: (action, api) => {
      const originalArgs = action.meta.arg.originalArgs as DeleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdApiArg
      const marketId = originalArgs.marketId as EnhancedMarketId
      const groupId = originalArgs.groupId

      api.dispatch(
        _pushOrReplaceGroup({
          marketId,
          groupId,
          group: null,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.postApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamilies.matchFulfilled,
    effect: (action, api) => {
      // console.log('Done POST /admintool/markets/{marketId}/groups/{groupId}/families', action, api)
      const originalArgs = action.meta.arg.originalArgs as PostApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesApiArg
      const marketId = originalArgs.marketId as EnhancedMarketId
      const groupId = originalArgs.groupId
      const familyId = originalArgs.adminNatCodesNewFamilyRequest.familyId

      const returnValue = action.payload.family
      if (!returnValue) {
        toastError('No family returned from POST /admintool/markets/{marketId}/groups/{groupId}/families')
        return
      }

      const { codes, ...family } = returnValue

      api.dispatch(
        _pushOrReplaceFamily({
          marketId,
          groupId,
          familyId,
          family,
        }),
      )

      if (codes) {
        for (const code of codes) {
          if (!code.executionId) {
            continue
          }

          api.dispatch(
            _pushOrReplaceVariant({
              marketId,
              groupId,
              familyId,
              executionId: code.executionId,
              code,
            }),
          )
        }
      }
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.putApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyId.matchFulfilled,
    effect: (action, api) => {
      // console.log('Done PUT /admintool/markets/{marketId}/groups/{groupId}/families/{familyId}', action, api)
      const originalArgs = action.meta.arg.originalArgs as PutApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdApiArg
      const marketId = originalArgs.marketId as EnhancedMarketId
      const groupId = originalArgs.groupId
      const familyId = originalArgs.familyId

      const returnValue = action.payload.family
      if (!returnValue) {
        toastError('No family returned from PUT /admintool/markets/{marketId}/groups/{groupId}/families/{familyId}')
        return
      }
      const { codes: _codes, ...family } = returnValue

      api.dispatch(
        _pushOrReplaceFamily({
          marketId,
          groupId,
          familyId,
          family,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.deleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyId.matchFulfilled,
    effect: (action, api) => {
      const originalArgs = action.meta.arg.originalArgs as DeleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdApiArg
      const marketId = originalArgs.marketId as EnhancedMarketId
      const groupId = originalArgs.groupId
      const familyId = originalArgs.familyId

      api.dispatch(
        _pushOrReplaceFamily({
          marketId,
          groupId,
          familyId,
          family: null,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.postApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutions.matchFulfilled,
    effect: (action, api) => {
      // console.log('Done POST /admintool/markets/{marketId}/groups/{groupId}/families/{familyId}/codes', action, api)
      const originalArgs = action.meta.arg.originalArgs as PostApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsApiArg
      const marketId = originalArgs.marketId as EnhancedMarketId
      const groupId = originalArgs.groupId
      const familyId = originalArgs.familyId
      const executionId = originalArgs.adminNatCodesNewCodeRequest.executionId

      const code = action.payload.code
      if (!code) {
        toastError('No code returned from POST /admintool/markets/{marketId}/groups/{groupId}/families/{familyId}/codes')
        return
      }

      api.dispatch(
        _pushOrReplaceVariant({
          marketId,
          groupId,
          familyId,
          executionId,
          code,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.putApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionId.matchFulfilled,
    effect: (action, api) => {
      // console.log('Done PUT /admintool/markets/{marketId}/groups/{groupId}/families/{familyId}/codes/{codeId}', action, api)
      const originalArgs = action.meta.arg.originalArgs as PutApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionIdApiArg
      const marketId = originalArgs.marketId as EnhancedMarketId
      const groupId = originalArgs.groupId
      const familyId = originalArgs.familyId
      const executionId = originalArgs.executionId

      const code = action.payload.code
      if (!code) {
        toastError('No code returned from PUT /admintool/markets/{marketId}/groups/{groupId}/families/{familyId}/codes/{codeId}')
        return
      }

      api.dispatch(
        _pushOrReplaceVariant({
          marketId,
          groupId,
          familyId,
          executionId,
          code,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.deleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionId.matchFulfilled,
    effect: (action, api) => {
      const originalArgs = action.meta.arg
        .originalArgs as DeleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionIdApiArg
      const marketId = originalArgs.marketId as EnhancedMarketId
      const groupId = originalArgs.groupId
      const familyId = originalArgs.familyId
      const executionId = originalArgs.executionId

      api.dispatch(
        _pushOrReplaceVariant({
          marketId,
          groupId,
          familyId,
          executionId,
          code: null,
        }),
      )
    },
  })

  registerPendingHandlers(middleware, enhancedTmsApiService.endpoints.putApiAdmintoolMarketsByMarketIdGroupsAndGroupId, (action) => {
    const originalArgs = action.meta.arg.originalArgs as PutApiAdmintoolMarketsByMarketIdGroupsAndGroupIdApiArg
    return {
      marketId: originalArgs.marketId as EnhancedMarketId,
      taskId: originalArgs.groupId,
    }
  })

  registerPendingHandlers(middleware, enhancedTmsApiService.endpoints.putApiAdmintoolMarketsByMarketIdGroupsAndGroupId, (action) => {
    const originalArgs = action.meta.arg.originalArgs as DeleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdApiArg
    return {
      marketId: originalArgs.marketId as EnhancedMarketId,
      taskId: originalArgs.groupId,
    }
  })

  registerPendingHandlers(middleware, enhancedTmsApiService.endpoints.putApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyId, (action) => {
    const originalArgs = action.meta.arg.originalArgs as PutApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdApiArg
    return {
      marketId: originalArgs.marketId as EnhancedMarketId,
      taskId: originalArgs.familyId,
    }
  })

  registerPendingHandlers(
    middleware,
    enhancedTmsApiService.endpoints.deleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionId,
    (action) => {
      const originalArgs = action.meta.arg.originalArgs as PutApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdApiArg
      return {
        marketId: originalArgs.marketId as EnhancedMarketId,
        taskId: originalArgs.familyId,
      }
    },
  )

  registerPendingHandlers(
    middleware,
    enhancedTmsApiService.endpoints.putApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionId,
    (action) => {
      const originalArgs = action.meta.arg.originalArgs as PutApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionIdApiArg
      return {
        marketId: originalArgs.marketId as EnhancedMarketId,
        taskId: `${originalArgs.familyId}${originalArgs.executionId}`,
      }
    },
  )

  registerPendingHandlers(
    middleware,
    enhancedTmsApiService.endpoints.deleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionId,
    (action) => {
      const originalArgs = action.meta.arg
        .originalArgs as DeleteApiAdmintoolMarketsByMarketIdGroupsAndGroupIdFamiliesFamilyIdExecutionsExecutionIdApiArg
      const taskId = `${originalArgs.familyId}${originalArgs.executionId}`
      return {
        marketId: originalArgs.marketId as EnhancedMarketId,
        taskId: taskId,
      }
    },
  )

  return middleware
})()

type EndpointKeys = keyof typeof enhancedTmsApiService.endpoints
type Endpoint = (typeof enhancedTmsApiService.endpoints)[EndpointKeys]
// TODO it would be really nice to derive this from the endpoint type, but I don't know how to do that
type EndpointAction = PayloadAction<unknown, string, { arg: { originalArgs: unknown } }>

function registerPendingHandlers<E extends Endpoint>(
  middleware: ReturnType<typeof createListenerMiddleware>,
  endpoint: E,
  deriveTaskId: (action: EndpointAction) => {
    marketId: EnhancedMarketId
    taskId: string
  },
) {
  middleware.startListening({
    matcher: endpoint.matchPending,
    effect: (action, api) => {
      const { marketId, taskId } = deriveTaskId(action)
      api.dispatch(_addPendingTask({ marketId, taskId }))
    },
  })

  middleware.startListening({
    matcher: endpoint.matchFulfilled,
    effect: (action, api) => {
      const { marketId, taskId } = deriveTaskId(action)
      api.dispatch(_removePendingTask({ marketId, taskId }))
    },
  })

  middleware.startListening({
    matcher: endpoint.matchRejected,
    effect: (action, api) => {
      const { marketId, taskId } = deriveTaskId(action)
      api.dispatch(_removePendingTask({ marketId, taskId }))
    },
  })
}
